import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Grid
} from "@mui/material";
import {
  Zap,
} from 'lucide-react';
import Constants from "../../utils/constants/Constants";
import { tokens } from "../../theme";

const TakeActionForm = (props) => {
  const { handleAction, toggleModal, reservation } = props;
  const [action, setAction] = useState("");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const allowedActions =
    Constants.STATUS_UPDATE_ALLOWED_ACTIONS[reservation.status] ?? [];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 4 },
        borderRadius: "20px",
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <Zap size={24} color={colors.greenAccent[500]} />
        <Typography variant="h4" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
          Decision Nexus
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ color: colors.grey[300], mb: 4 }}>
        Determine the next phase for Reservation <strong>#{reservation.id}</strong>.
      </Typography>

        {allowedActions.length === 0 && (
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3 }}>
            No workflow actions are currently available for reservations in the <strong>{reservation.status}</strong> status.
          </Typography>
        )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="action-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Strategic Action</InputLabel>
            <Select
              labelId="action-label"
              id="action"
              value={action}
              label="Strategic Action"
              onChange={(e) => setAction(e.target.value)}
              disabled={allowedActions.length === 0}
              sx={{
                borderRadius: "12px",
                color: colors.grey[100],
                "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                "& .MuiSvgIcon-root": { color: colors.grey[100] }
              }}
            >
              <MenuItem value="" sx={{ color: colors.grey[100] }}>Select Action</MenuItem>
              {allowedActions.map((actionKey) => (
                <MenuItem
                  key={actionKey}
                  value={Constants.STATUS_UPDATE_ACTIONS[actionKey]}
                  sx={{ color: colors.grey[100] }}
                >
                  {Constants.STATUS_UPDATE_ACTIONS[actionKey]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => toggleModal(false)}
            sx={{
              py: 1.5,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: "700",
              borderColor: colors.redAccent[500],
              color: colors.redAccent[500],
              "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!action || allowedActions.length === 0}
            onClick={() => handleAction(action)}
            sx={{
              py: 1.5,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: "700",
              bgcolor: colors.greenAccent[500],
              color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
              "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
            }}
          >
            Execute Action
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

TakeActionForm.propTypes = {
  handleAction: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  reservation: PropTypes.object.isRequired,
};

export default TakeActionForm;
